import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Stethoscope, 
  Plus, 
  User,
  Calendar,
  FileText,
  Activity,
  ClipboardList,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATO_BADGE = {
  ATTIVA: 'bg-green-100 text-green-800',
  CHIUSA: 'bg-slate-100 text-slate-800',
  ARCHIVIATA: 'bg-yellow-100 text-yellow-800'
};

const Ospedale = () => {
  const { user, hasPermesso, isSindacatoAdmin } = useAuth();
  const [cartelle, setCartelle] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refertoDialogOpen, setRefertoDialogOpen] = useState(false);
  const [selectedCartella, setSelectedCartella] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [form, setForm] = useState({
    paziente_id: '',
    diagnosi: '',
    terapia: '',
    note: ''
  });
  const [refertoForm, setRefertoForm] = useState({
    tipo: '',
    descrizione: '',
    esito: ''
  });

  const canGestisci = () => isSindacatoAdmin() || hasPermesso('gestisci_cartelle_cliniche');
  const canVisualizza = () => isSindacatoAdmin() || hasPermesso('visualizza_cartelle_cliniche') || hasPermesso('gestisci_cartelle_cliniche');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cartelleRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/ospedale/cartelle`),
        canGestisci() ? axios.get(`${API_URL}/users`) : Promise.resolve({ data: [] })
      ]);
      setCartelle(cartelleRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status !== 403) {
        toast.error('Errore nel caricamento dei dati');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCartella = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/ospedale/cartelle`, form);
      toast.success('Cartella clinica creata');
      setDialogOpen(false);
      setForm({ paziente_id: '', diagnosi: '', terapia: '', note: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleAddReferto = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/ospedale/cartelle/${selectedCartella.id}/referti`, {
        cartella_id: selectedCartella.id,
        ...refertoForm
      });
      toast.success('Referto aggiunto');
      setRefertoDialogOpen(false);
      setRefertoForm({ tipo: '', descrizione: '', esito: '' });
      setSelectedCartella(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta del referto');
    }
  };

  const handleUpdateStato = async (cartellaId, stato) => {
    try {
      await axios.patch(`${API_URL}/ospedale/cartelle/${cartellaId}?stato=${stato}`);
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const toggleExpand = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ospedale-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestione Ospedale</h1>
          <p className="text-slate-600">Cartelle cliniche e referti</p>
        </div>
        {canGestisci() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="nuova-cartella-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Cartella Clinica
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Nuova Cartella Clinica</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCartella} className="space-y-4">
                <div className="space-y-2">
                  <Label>Paziente</Label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={form.paziente_id}
                    onChange={(e) => setForm({...form, paziente_id: e.target.value})}
                    required
                  >
                    <option value="">Seleziona paziente...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.nome} {u.cognome}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Diagnosi</Label>
                  <Textarea
                    value={form.diagnosi}
                    onChange={(e) => setForm({...form, diagnosi: e.target.value})}
                    placeholder="Diagnosi del paziente..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Terapia</Label>
                  <Textarea
                    value={form.terapia}
                    onChange={(e) => setForm({...form, terapia: e.target.value})}
                    placeholder="Terapia prescritta..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Note (opzionale)</Label>
                  <Textarea
                    value={form.note}
                    onChange={(e) => setForm({...form, note: e.target.value})}
                    placeholder="Note aggiuntive..."
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                  <Button type="submit">Crea Cartella</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Lista Cartelle */}
      {cartelle.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Stethoscope className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Nessuna cartella clinica</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {cartelle.map((cartella) => (
            <Card key={cartella.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{cartella.paziente_nome}</CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        <Calendar className="w-3 h-3" />
                        Creata: {new Date(cartella.created_at).toLocaleDateString('it-IT')}
                        <span>•</span>
                        Medico: {cartella.medico_nome}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={STATO_BADGE[cartella.stato] || 'bg-slate-100'}>
                      {cartella.stato}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpand(cartella.id)}
                    >
                      {expandedCards[cartella.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Diagnosi</p>
                    <p className="text-sm text-slate-900">{cartella.diagnosi}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Terapia</p>
                    <p className="text-sm text-slate-900">{cartella.terapia}</p>
                  </div>
                </div>
                
                {cartella.note && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-slate-500">Note</p>
                    <p className="text-sm text-slate-600">{cartella.note}</p>
                  </div>
                )}

                {/* Referti (espandibili) */}
                {expandedCards[cartella.id] && (
                  <div className="mt-4 border-t pt-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-slate-900 flex items-center gap-2">
                        <ClipboardList className="w-4 h-4" />
                        Referti ({cartella.referti?.length || 0})
                      </h4>
                      {canGestisci() && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCartella(cartella);
                            setRefertoDialogOpen(true);
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Aggiungi Referto
                        </Button>
                      )}
                    </div>
                    
                    {cartella.referti?.length > 0 ? (
                      <div className="space-y-2">
                        {cartella.referti.map((referto, idx) => (
                          <div key={idx} className="bg-slate-50 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <Badge className="bg-purple-100 text-purple-800 mb-1">{referto.tipo}</Badge>
                                <p className="text-sm text-slate-900">{referto.descrizione}</p>
                                <p className="text-sm text-slate-600 mt-1"><strong>Esito:</strong> {referto.esito}</p>
                              </div>
                              <div className="text-xs text-slate-400">
                                {new Date(referto.created_at).toLocaleDateString('it-IT')}
                                <br />
                                {referto.medico_nome}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-4">Nessun referto</p>
                    )}
                  </div>
                )}

                {/* Azioni */}
                {canGestisci() && (
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    {cartella.stato === 'ATTIVA' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleUpdateStato(cartella.id, 'CHIUSA')}>
                          Chiudi Cartella
                        </Button>
                      </>
                    )}
                    {cartella.stato === 'CHIUSA' && (
                      <Button size="sm" variant="outline" onClick={() => handleUpdateStato(cartella.id, 'ARCHIVIATA')}>
                        Archivia
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Referto */}
      <Dialog open={refertoDialogOpen} onOpenChange={setRefertoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Referto</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddReferto} className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo Referto</Label>
              <select
                className="w-full border rounded-md p-2"
                value={refertoForm.tipo}
                onChange={(e) => setRefertoForm({...refertoForm, tipo: e.target.value})}
                required
              >
                <option value="">Seleziona tipo...</option>
                <option value="Esame del sangue">Esame del sangue</option>
                <option value="Radiografia">Radiografia</option>
                <option value="Ecografia">Ecografia</option>
                <option value="TAC">TAC</option>
                <option value="Risonanza Magnetica">Risonanza Magnetica</option>
                <option value="Visita specialistica">Visita specialistica</option>
                <option value="Altro">Altro</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={refertoForm.descrizione}
                onChange={(e) => setRefertoForm({...refertoForm, descrizione: e.target.value})}
                placeholder="Descrizione del referto..."
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Esito</Label>
              <Textarea
                value={refertoForm.esito}
                onChange={(e) => setRefertoForm({...refertoForm, esito: e.target.value})}
                placeholder="Esito del referto..."
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setRefertoDialogOpen(false)}>Annulla</Button>
              <Button type="submit">Aggiungi Referto</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Ospedale;
